import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { euro, dateFR } from "@/lib/format";
import { sendQuotePdfEmail } from "@/lib/pdfLazy";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { GeoFilters } from "@/components/GeoFilters";
import { inSel, MultiFilter } from "@/components/MultiFilter";

const KIND_MATCH = {
  quote: (x) => !x.signature && !x.historical,
  signed: (x) => !!x.signature || !!x.historical,
  nodivalto: (x) => !!x.signature && !x.divalto_order,
  insector: (x) => x.out_of_sector === false,
  oos: (x) => !!x.out_of_sector,
  paper: (x) => !!x.paper_order,
};
const KINDS = [["quote", "Devis"], ["signed", "Commandes signées"], ["nodivalto", "Commandes sans numéro cde Divalto"], ["insector", "Commandes secteur"], ["oos", "Commandes hors secteur"], ["paper", "Bon papier"]];
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Mail, Trash2, FileText, Copy, PenLine, SlidersHorizontal, Link2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { setActAs } from "@/lib/actAs";
import { SignView } from "@/components/SignView";
import { PosterPreview } from "@/components/PosterPreview";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { ConfirmDeleteSigned } from "@/components/ConfirmDeleteSigned";
import { ResellerDiscountDialog } from "@/components/ResellerDiscountDialog";
import { Percent, Landmark, Download, FilePenLine, Wrench, UserPen, FileCheck2 } from "lucide-react";
import { OrderClientDialog } from "@/components/OrderClientDialog";
import { AdminCorrectDialog } from "@/components/AdminCorrectDialog";
import { ReplaceBadge } from "@/components/ReplaceBadge";
import { SortHead } from "@/components/SortHead";
import { QuotesTrash } from "@/components/QuotesTrash";

const SORTERS = {
  number: (x) => x.quote_number || "",
  date: (x) => x.created_at || "",
  seller: (x) => x.reseller_name || "",
  model: (x) => x.model_name || "",
  client: (x) => x.client?.name || "",
  total: (x) => Number(x.total_public_ttc) || 0,
  cost: (x) => Number(x.total_reseller_ht) || 0,
  margin: (x) => Number(x.margin_ht) || 0,
  status: (x) => x.status || "",
  owner: (x) => (x.sector_owners || []).join(", "),
};


import { useI18n } from "@/context/I18nContext";
import { RibDialog } from "@/components/RibDialog";
import { SendLog } from "@/components/SendLog";
import { OrderTracking } from "@/components/OrderTracking";
import { OrderRefs } from "@/components/OrderRefs";
import { exportOrdersCsv } from "@/lib/exportOrders";
import { SendIssuesBadge, hasOpenIssue, issuesOf } from "@/components/EmailRejectedBadge";
import { AlertTriangle, Search, RotateCcw, Ban } from "lucide-react";
import { copySignLink } from "@/lib/copyLink";
import { VehicleSourceDialog, CopyLinkConfirm } from "@/components/VehicleSourceDialog";

const STATUS = ["Brouillon", "Envoyé", "Confirmé", "En Fabrication"];
const statusColor = {
  "Brouillon": "bg-slate-500/15 text-slate-300",
  "Envoyé": "bg-blue-500/15 text-blue-400",
  "Confirmé": "bg-[#9E7B41]/15 text-[#c9a227]",
  "En Fabrication": "bg-amber-500/15 text-amber-400",
};

const FinancedSummary = ({ quotes }) => {
  const total = quotes.reduce((s, x) => s + (Number(x.financing?.montant) || 0), 0);
  const bySeller = Object.entries(quotes.reduce((acc, x) => {
    const k = x.reseller_name || "—";
    acc[k] = acc[k] || { n: 0, sum: 0 };
    acc[k].n += 1; acc[k].sum += Number(x.financing?.montant) || 0;
    return acc;
  }, {})).sort((a, b) => b[1].sum - a[1].sum);
  const exportCsv = () => {
    const num = (v) => (v == null || v === "" ? "" : String(Math.round(Number(v) * 100) / 100).replace(".", ","));
    const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = ["N° devis", "Date", "Vendeur", "Client", "Modèle", "Statut", "Solution", "Organisme", "Montant financé (€)", "Durée (mois)", "Mensualité (€)", "TAEG (%)", "Total TTC (€)"];
    const rows = quotes.map((x) => {
      const f = x.financing || {};
      const client = [x.client?.name, x.client?.firstname].filter(Boolean).join(" ") || x.client?.company || "";
      return [x.quote_number, dateFR(x.created_at), x.reseller_name, client, x.model_name, x.status, f.solution, f.provider,
        num(f.montant), f.duree, num(f.mensualite), f.taeg != null ? num(f.taeg * 100) : "", num(x.total_public_ttc)];
    });
    const csv = "\ufeff" + [head, ...rows].map((r) => r.map(cell).join(";")).join("\r\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    a.download = `devis-finances_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div className="mb-4 rounded-xl border border-[#9E7B41]/40 bg-[#9E7B41]/5 p-4" data-testid="financed-summary">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <Button size="sm" variant="outline" className="order-last ml-auto gap-2" disabled={!quotes.length} onClick={exportCsv} data-testid="financed-export">
          <Download className="w-4 h-4" /> Télécharger (tableur)
        </Button>
        <div className="text-sm">Montant total financé : <span className="font-mono font-semibold text-[#9E7B41]" data-testid="financed-total">{euro(total)}</span></div>
        <div className="text-sm text-muted-foreground" data-testid="financed-total-count">{quotes.length} devis financé{quotes.length > 1 ? "s" : ""}</div>
      </div>
      {bySeller.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {bySeller.map(([name, v]) => (
            <div key={name} className="rounded-md border border-border bg-background px-2.5 py-1 text-xs" data-testid={`financed-seller-${name}`}>
              <span className="font-medium">{name}</span> · {v.n} devis · <span className="font-mono">{euro(v.sum)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function QuoteHistory() {
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const prefsKey = `fautras_quotes_view_${user?.id || ""}`;
  const saved = (() => { try { return JSON.parse(localStorage.getItem(prefsKey)) || {}; } catch { return {}; } })();
  const [q, setQ] = useState(saved.q ?? "");
  const arr = (v) => (Array.isArray(v) ? v : null);
  const [filter, setFilter] = useState(arr(saved.statusSel));
  const [financedOnly, setFinancedOnly] = useState(!!saved.financedOnly);
  const [rejectedOnly, setRejectedOnly] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [kind, setKind] = useState(arr(saved.kindSel));
  const [sort, setSort] = useState(saved.sort ?? { key: "", dir: 1 });
  const [signTarget, setSignTarget] = useState(null);
  const [srcTarget, setSrcTarget] = useState(null);
  const [srcNext, setSrcNext] = useState(() => () => {});
  const [copyTarget, setCopyTarget] = useState(null);
  const [orderPdf, setOrderPdf] = useState(null);
  const [discTarget, setDiscTarget] = useState(null);
  const [fixTarget, setFixTarget] = useState(null);
  const [ocTarget, setOcTarget] = useState(null);
  const [seller, setSeller] = useState(user?.role === "admin" || user?.adv ? arr(saved.sellerSel) : ["__me__"]);
  const [country, setCountry] = useState(saved.country ?? null);
  const [range, setRange] = useState(saved.range ?? null);
  const [dateFrom, setDateFrom] = useState(saved.dateFrom ?? "");
  const [dateTo, setDateTo] = useState(saved.dateTo ?? "");
  useEffect(() => {
    localStorage.setItem(prefsKey, JSON.stringify({ q, statusSel: filter, financedOnly, kindSel: kind, sort, sellerSel: seller, country, range, dateFrom, dateTo }));
  }, [prefsKey, q, filter, financedOnly, kind, sort, seller, country, range, dateFrom, dateTo]);
  const isAdmin = user?.role === "admin";
  const manager = !!user?.sales_manager && !isAdmin;
  const showSeller = isAdmin || manager || quotes.some((x) => x.read_only);
  const mine = (x) => isAdmin || manager || x.reseller_id === user?.id || !x.reseller_id;
  // Manager / admin opening a reseller's quote: work as that reseller (his tariffs, margin, delivery places)
  const openConfig = (x, replace = false) => {
    const forOther = (isAdmin || user?.sales_manager) && x.reseller_id && x.reseller_id !== user?.id && !x.stock_order;
    setActAs(forOther ? { id: x.reseller_id, name: x.reseller_name } : null);
    if (!x.model_id) {
      // Brouillon sans modèle (fiche client seule) : reprendre au choix du modèle
      localStorage.setItem("fautras_client", JSON.stringify(x.client || {}));
      localStorage.setItem("fautras_draft_id", x.id);
      if (x.client_id) localStorage.setItem("fautras_client_id", x.client_id); else localStorage.removeItem("fautras_client_id");
      nav("/selection");
      return;
    }
    nav(`/configurer?quote=${x.id}${replace ? "&replace=1" : ""}`);
  };
  // Modifier une commande signée : reprend la modification en cours s'il y en a une
  const modifyOrder = (x) => {
    const pend = x.suspended_by && quotes.find((q) => q.id === x.suspended_by);
    if (pend) openConfig(pend); else openConfig(x, true);
  };
  const sellers = useMemo(() => {
    const m = new Map();
    for (const x of quotes) {
      const id = x.reseller_id || x.reseller_name;
      const name = x.stock_order ? [x.client?.firstname, x.client?.name].filter(Boolean).join(" ") : x.reseller_name;
      if (id && name && (!m.has(id) || (m.get(id).stock && !x.stock_order))) m.set(id, { id, name, stock: !!x.stock_order });
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base" }));
  }, [quotes]);
  const viewSigned = async (quote) => {
    if (!quote.pdf_path) { toast.error(t("PDF de la commande signée introuvable")); return; }
    try {
      const { data } = await api.get(`/files/${quote.pdf_path}`, { responseType: "arraybuffer" });
      const save = () => { const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([data], { type: "application/pdf" })); a.download = `Commande_${quote.quote_number}.pdf`; a.click(); };
      setOrderPdf({ data, save, email: (quote.client || {}).email || "" });
    } catch { toast.error(t("PDF de la commande signée introuvable")); }
  };

  const openFile = async (f) => {
    try {
      const { data } = await api.get(`/files/${f.path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([data], { type: f.content_type })), "_blank");
    } catch { toast.error(t("Fichier introuvable")); }
  };
  const openSign = async (quote) => {
    const col = (quote.color || "").trim();
    if (!quote.signature && (!col || (/ral/i.test(col) && !col.includes(" : ")))) {
      try {
        const { data } = await api.get(`/catalog/${quote.range_key}`);
        if ((data.models || []).find((m) => m.id === quote.model_id)?.colors?.length) {
          toast.error("Couleur non choisie : ouvrez le devis dans le configurateur pour choisir une couleur avant de passer commande.");
          return;
        }
      } catch { /* backend re-checks at signature */ }
    }
    if (!quote.vehicle_source) { setSrcTarget(quote); setSrcNext(() => (q) => setSignTarget(q)); return; }
    setSignTarget(quote);
  };
  const saveSignature = async (sigValue, cgvSig, clientEmail) => {
    try {
      const now = new Date().toISOString();
      const { data } = await api.put(`/quotes/${signTarget.id}/signature`, { signature: sigValue, signed_at: now, cgv_accepted_at: now, cgv_signature: cgvSig, client_email: clientEmail || "" });
      setQuotes((qs) => qs.map((x) => (x.id === data.id ? data : x)));
      setSignTarget(null);
      if (!sigValue) { toast.success("Signature retirée"); return; }
      const sent = await sendQuotePdfEmail(data.id);
      setQuotes((qs) => qs.map((x) => (x.id === data.id ? { ...x, status: sent.status || x.status } : x)));
      toast.success(`Signature enregistrée — copie envoyée à ${sent.count} destinataire(s)`);
      setOrderPdf({ ...sent.pdf, email: (data.client || {}).email || "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const savePaper = async (files) => {
    const tid = toast.loading(t("Enregistrement de la commande papier…"));
    try {
      const { data } = await api.put(`/quotes/${signTarget.id}/paper-order`, { files });
      setQuotes((qs) => qs.map((x) => (x.id === data.id ? data : x)));
      setSignTarget(null);
      const sent = await sendQuotePdfEmail(data.id).catch((err) => {
        throw Object.assign(err, { hint: t("Commande papier enregistrée, mais l'e-mail n'est pas parti : renvoyez-la avec le bouton e-mail de la ligne.") });
      });
      setQuotes((qs) => qs.map((x) => (x.id === data.id ? { ...x, status: sent.status || x.status } : x)));
      toast.success(`Commande papier enregistrée — envoyée à ${sent.count} destinataire(s)`, { id: tid });
      setOrderPdf({ ...sent.pdf, email: "", paper: true });
    } catch (err) {
      toast.error(err.hint || formatApiErrorDetail(err.response?.data?.detail), { id: tid, duration: err.hint ? 12000 : undefined });
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/quotes").then(({ data }) => setQuotes(data)).finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => {
    const back = () => load();
    window.addEventListener("net:back", back);
    return () => { window.removeEventListener("net:back", back); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, status) => {
    try {
      await api.put(`/quotes/${id}/status`, { status });
      setQuotes((qs) => qs.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Statut mis à jour");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const [ribQuote, setRibQuote] = useState(null);
  const send = (quote) => setRibQuote(quote);
  const saveSource = async (v, loc = "") => {
    try {
      const { data } = await api.put(`/quotes/${srcTarget.id}/vehicle-source`, { vehicle_source: v, stock_location: loc });
      setQuotes((qs) => qs.map((x) => (x.id === data.id ? { ...x, vehicle_source: v, stock_location: data.stock_location } : x)));
      setSrcTarget(null);
      srcNext({ ...srcTarget, vehicle_source: v, stock_location: data.stock_location });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const copyLink = async (quote) => {
    if (!quote.vehicle_source) { setSrcTarget(quote); setSrcNext(() => (q) => setCopyTarget(q)); return; }
    try {
      await copySignLink(quote);
      toast.success(t("Lien du devis copié : collez-le dans votre message au client"));
      load();
    } catch (err) { contactErr(err, quote) || toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("Impossible de copier le lien")); }
  };
  const contactErr = (err, quote, id) => {
    const d = formatApiErrorDetail(err.response?.data?.detail) || "";
    if (!d.startsWith("Coordonnées du client incomplètes")) return false;
    toast.error(d, { id, duration: 10000, action: { label: t("Compléter"), onClick: () => nav(`/configurer?quote=${quote.id}&contact=1`) } });
    return true;
  };
  const doSend = async (quote, ribPath, bank) => {
    const t = toast.loading("Envoi…");
    try {
      const data = await sendQuotePdfEmail(quote.id, ribPath, bank, load);
      if (data.simulated && !data.count) toast.dismiss(t);
      else toast.success(data.count ? `Envoyé à ${data.count} destinataire(s)` : "Devis envoyé", { id: t });
      if (data.failed?.length && !data.refused?.length) toast.error(`Échec de l'envoi à : ${data.failed.join(", ")}`);
      load();
      setQuotes((qs) => qs.map((x) => (x.id === quote.id ? { ...x, status: data.status || x.status } : x)));
    } catch (err) {
      if (contactErr(err, quote, t)) return;
      toast.error(formatApiErrorDetail(err.response?.data?.detail), { id: t });
    }
  };

  const del = async (id, confirm = "") => {
    try {
      await api.delete(`/quotes/${id}`, { params: confirm ? { confirm } : {} });
      setQuotes((qs) => qs.filter((x) => x.id !== id));
      toast.success(confirm ? "Commande signée mise à la corbeille (30 jours)" : "Devis mis à la corbeille (30 jours)");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/quotes/${id}/duplicate`);
      setQuotes((qs) => [data, ...qs]);
      toast.success(`Devis dupliqué → ${data.quote_number}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filtered = quotes.filter((x) => {
    const okStatus = inSel(filter, x.status);
    const term = q.toLowerCase();
    const okTerm = !term ||
      x.quote_number.toLowerCase().includes(term) ||
      x.model_name.toLowerCase().includes(term) ||
      (x.serial_number || "").toLowerCase().includes(term) ||
      (x.divalto_order || "").toLowerCase().includes(term) ||
      (x.client_divalto_code || "").toLowerCase().includes(term) ||
      (x.reseller_ref || "").toLowerCase().includes(term) ||
      (x.client?.name || "").toLowerCase().includes(term);
    const okFin = !financedOnly || x.financing?.mensualite > 0;
    const okKind = kind === null || kind.some((k) => KIND_MATCH[k](x));
    const okRej = !rejectedOnly || hasOpenIssue(x);
    const okGeo = inSel(country, x.country_code || "FR") && inSel(range, x.range_key);
    const day = (x.created_at || "").slice(0, 10);
    const okDate = (!dateFrom || day >= dateFrom) && (!dateTo || day <= dateTo);
    const okSeller = seller === null || seller.some((s) => (s === "__me__" ? (x.reseller_id === user?.id || x.entered_by_id === user?.id) : (x.reseller_id || x.reseller_name) === s));
    return (showCancelled || !x.cancelled) && okStatus && okTerm && okFin && okKind && okRej && okGeo && okDate && okSeller;
  });
  if (sort.key) {
    const get = SORTERS[sort.key];
    filtered.sort((a, b) => {
      const va = get(a), vb = get(b);
      return sort.dir * (typeof va === "number" ? va - vb : String(va).localeCompare(String(vb), "fr", { numeric: true, sensitivity: "base" }));
    });
  }
  const financedCount = quotes.filter((x) => x.financing?.mensualite > 0).length;

  return (
    <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">{t("Mes devis")}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t(isAdmin ? "Tous les devis des revendeurs" : manager ? "Vos devis et ceux des vendeurs France et Suisse" : "Vos devis et ceux des autres revendeurs de votre pays (consultation seule)")}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input placeholder={t("Rechercher…")} value={q} onChange={(e) => setQ(e.target.value)}
              title={t("N°, client, modèle, n° de série")} data-testid="quote-search" className="pl-9 w-full" />
          </div>
          {showSeller && (
            <MultiFilter label={t("Vendeur")} sel={seller} onChange={setSeller} testid="seller-filter" className="w-56"
              options={[...(!isAdmin ? [{ value: "__me__", label: t("Mes devis") }] : []), ...sellers.map((s) => ({ value: s.id, label: s.name }))]} />
          )}
          <GeoFilters items={quotes.map((x) => ({ country: x.country_code || "FR", ranges: [x.range_key] }))} country={country} setCountry={setCountry} range={range} setRange={setRange} prefix="quotes" />
          <div className="flex items-center gap-1.5" data-testid="date-filter">
            <span className="text-xs text-muted-foreground">{t("Du")}</span>
            <Input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} className="w-[9.5rem]" data-testid="date-from" />
            <span className="text-xs text-muted-foreground">{t("au")}</span>
            <Input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} className="w-[9.5rem]" data-testid="date-to" />
          </div>
          <MultiFilter label={t("Type")} sel={kind} onChange={setKind} testid="kind-filter" className="w-56"
            options={KINDS.map(([v, l]) => ({ value: v, label: `${t(l)} (${quotes.filter(KIND_MATCH[v]).length})` }))} />
          <MultiFilter label={t("Statut")} sel={filter} onChange={setFilter} testid="status-filter" className="w-48"
            options={STATUS.map((s) => ({ value: s, label: s }))} />
          <Button variant={financedOnly ? "default" : "outline"} className="gap-2" onClick={() => setFinancedOnly((v) => !v)}
            aria-pressed={financedOnly} data-testid="financed-filter">
            <Landmark className="w-4 h-4" /> Devis financés <span className="font-mono text-xs opacity-80" data-testid="financed-count">{financedCount}</span>
          </Button>
          <Button variant={showCancelled ? "default" : "outline"} className="gap-2" onClick={() => setShowCancelled((v) => !v)}
            aria-pressed={showCancelled} data-testid="cancelled-filter">
            <Ban className="w-4 h-4" /> {t("Commandes annulées")} <span className="font-mono text-xs opacity-80" data-testid="cancelled-count">{quotes.filter((x) => x.cancelled).length}</span>
          </Button>
          {(rejectedOnly || quotes.some(hasOpenIssue)) && (
            <Button variant={rejectedOnly ? "destructive" : "outline"} className={`gap-2 ${rejectedOnly ? "" : "text-destructive border-destructive/40"}`}
              onClick={() => setRejectedOnly((v) => !v)} aria-pressed={rejectedOnly} data-testid="email-rejected-filter">
              <AlertTriangle className="w-4 h-4" /> {t("Emails à vérifier")} <span className="font-mono text-xs opacity-80" data-testid="email-rejected-count">{quotes.filter(hasOpenIssue).length}</span>
            </Button>
          )}
          <QuotesTrash onRestored={load} />
          {(q || filter !== null || financedOnly || kind !== null || sort.key || seller !== null || country !== null || range !== null || dateFrom || dateTo) && (
            <Button variant="outline" className="gap-2 border-amber-500 bg-amber-50 text-amber-800 font-semibold hover:bg-amber-100" data-testid="reset-view"
              onClick={() => { setQ(""); setFilter(null); setFinancedOnly(false); setKind(null); setSort({ key: "", dir: 1 }); setSeller(null); setCountry(null); setRange(null); setDateFrom(""); setDateTo(""); }}>
              <RotateCcw className="w-4 h-4" /> {t("Réinitialiser les filtres")}
            </Button>
          )}
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground" data-testid="quotes-count">
          <span className="font-semibold text-foreground">{filtered.length}</span> {filtered.length > 1 ? t("devis affichés") : t("devis affiché")}
          {filtered.length !== quotes.length && <> {t("sur")} {quotes.length}</>}
          {filtered.some(KIND_MATCH.signed) && <> · {filtered.filter(KIND_MATCH.signed).length} {t("commande(s)")}</>}
        </div>
        {(isAdmin || user?.sales_manager) && filtered.some((x) => !!x.signature) && (
          <Button size="sm" variant="outline" className="gap-2" onClick={() => exportOrdersCsv(filtered.filter((x) => !!x.signature))} data-testid="orders-export">
            <Download className="w-4 h-4" /> {t("Exporter les commandes (Excel)")}
          </Button>
        )}
      </div>
      {financedOnly && <FinancedSummary quotes={filtered} />}

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-border rounded-xl">
          <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t(quotes.length ? "Aucun devis ne correspond aux filtres" : "Aucun devis pour le moment")}</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <SortHead k="client" sort={sort} setSort={setSort}>{t("Client")}</SortHead>
                {showSeller && <SortHead k="seller" sort={sort} setSort={setSort}>{t("Revendeur")}</SortHead>}
                <SortHead k="owner" sort={sort} setSort={setSort}>{t("Responsable du secteur")}</SortHead>
                <SortHead k="date" sort={sort} setSort={setSort}>{t("Date")}</SortHead>
                <SortHead k="model" sort={sort} setSort={setSort}>{t("Modèle")}</SortHead>
                <SortHead k="total" sort={sort} setSort={setSort} className="text-right">{t("Total TTC")}</SortHead>
                {manager && <SortHead k="cost" sort={sort} setSort={setSort} className="text-right">{t("Coût rev. HT")}</SortHead>}
                {manager && <SortHead k="margin" sort={sort} setSort={setSort} className="text-right">{t("Marge HT")}</SortHead>}
                <SortHead k="status" sort={sort} setSort={setSort}>{t("Statut")}</SortHead>
                <TableHead className="text-right">{t("Actions")}</TableHead>
                <SortHead k="number" sort={sort} setSort={setSort}>N°</SortHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((quote) => (
                <TableRow key={quote.id} data-testid={`quote-row-${quote.quote_number}`}>
                  <TableCell className="text-sm">{quote.client?.name || "—"}
                    {issuesOf(quote).length > 0 && <SendIssuesBadge quote={quote} t={t} onChanged={load}
                      onSaved={(id, email) => { setQuotes((qs) => qs.map((x) => (x.id === id ? { ...x, email_rejected: "", client: { ...x.client, email } } : x))); load(); }} />}
                  </TableCell>
                  {showSeller && <TableCell className="text-sm">{quote.stock_order ? [quote.client?.firstname, quote.client?.name].filter(Boolean).join(" ") : quote.reseller_name}
                    {quote.entered_by_name && <div className="text-[10px] text-muted-foreground" data-testid={`entered-by-${quote.quote_number}`}>{t("Saisi par")} {quote.entered_by_name}</div>}</TableCell>}
                  <TableCell className="text-sm" data-testid={`owner-${quote.quote_number}`}>{(quote.sector_owners || []).join(", ") || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{dateFR(quote.created_at)}</TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate">{quote.model_name}
                    {quote.read_only && <div className="mt-0.5"><Badge variant="outline" className="text-[10px]" data-testid={`read-only-badge-${quote.quote_number}`}>{t("Consultation seule")}</Badge></div>}
                    {quote.paper_order && <div className="mt-0.5"><Badge variant="outline" className="text-[10px] border-[#9E7B41] text-[#9E7B41]" data-testid={`paper-badge-${quote.quote_number}`}>{t("Bon papier")}</Badge>
                      {(quote.paper_files || []).map((f, i) => (
                        <button key={f.path} type="button" onClick={() => openFile(f)} className="ml-1 px-1.5 py-1 text-xs underline text-[#9E7B41]" data-testid={`paper-file-link-${quote.quote_number}-${i}`}>{t(f.content_type === "application/pdf" ? "Scan" : "Photo")} {i + 1}</button>))}</div>}
                    {quote.stock_order && <div className="mt-0.5"><Badge variant="outline" className="text-[10px] border-primary text-primary" data-testid={`stock-order-badge-${quote.quote_number}`}>{t(quote.stock_order === "demo" ? "Véhicule de démo" : "Commande stock")}</Badge></div>}
                    {quote.vehicle_source && <div className="text-[10px] text-muted-foreground" data-testid={`vehicle-source-${quote.quote_number}`}>{t(quote.vehicle_source === "stock" ? "Véhicule en stock" : "Véhicule à fabriquer")}{quote.vehicle_source === "stock" && quote.stock_location ? ` · ${quote.stock_location}` : ""}</div>}
                    {quote.vehicle_source !== "fabrication" && quote.serial_number && <div className="text-[10px] text-muted-foreground font-mono" data-testid={`quote-serial-${quote.quote_number}`}>{t("N° de série")} : {quote.serial_number}</div>}
                  </TableCell>
                  <TableCell className="text-right font-mono text-[#c9a227]">{euro(quote.total_public_ttc)}
                    {quote.reg_fees_ttc > 0 && <div className="text-[10px] text-muted-foreground font-sans whitespace-nowrap" data-testid={`reg-fees-${quote.quote_number}`}>+ {euro(quote.reg_fees_ttc)} {t("immat.")}</div>}
                    {quote.financing?.mensualite > 0 && (
                      <div><Badge variant="outline" className="mt-1 border-[#9E7B41]/50 text-[#9E7B41] text-[10px] font-medium whitespace-nowrap" data-testid={`financed-badge-${quote.quote_number}`}
                        title={`${quote.financing.solution || "Financement"}${quote.financing.provider ? ` (${quote.financing.provider})` : ""}`}>
                        Financé · {quote.financing.duree} × {euro(quote.financing.mensualite)}
                      </Badge></div>
                    )}
                  </TableCell>
                  {manager && <TableCell className="text-right font-mono text-sm" data-testid={`cost-${quote.quote_number}`}>{euro(quote.total_reseller_ht)}{quote.reseller_discount_pct > 0 && <div className="text-[10px] text-[#c9a227]">remise {quote.reseller_discount_pct} %</div>}</TableCell>}
                  {manager && <TableCell className="text-right font-mono text-sm" data-testid={`margin-${quote.quote_number}`}>{euro(quote.margin_ht)}</TableCell>}
                  <TableCell>
                    {!mine(quote) || quote.cancelled ? (
                      <span className={`text-xs px-2 py-1 rounded ${statusColor[quote.status] || ""}`} data-testid={`status-${quote.quote_number}`}>{quote.status}</span>
                    ) : (
                    <Select value={quote.status} onValueChange={(v) => setStatus(quote.id, v)}>
                      <SelectTrigger className={`h-7 w-auto border-0 ${statusColor[quote.status] || ""}`} data-testid={`status-${quote.quote_number}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS.map((s) => <SelectItem key={s} value={s} disabled={!!quote.signature && !["Confirmé", "En Fabrication"].includes(s)}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    )}
                    <ReplaceBadge quote={quote} />
                    {!manager && quote.reseller_discount_pct > 0 && <div className="text-[10px] text-[#c9a227] mt-0.5" data-testid={`rd-badge-${quote.quote_number}`}>Remise revendeur {quote.reseller_discount_pct} %</div>}
                    {!!quote.signature && !quote.stock_order && <OrderTracking quote={quote} editable={!quote.read_only && !quote.cancelled && (isAdmin || user?.sales_manager || (user?.dealer_type !== "agent" && !quote.out_of_sector && !quote.has_financing))}
                      canFinance={isAdmin || !!user?.sales_manager}
                      onChange={(d) => setQuotes((qs) => qs.map((x) => (x.id === d.id ? { ...x, ...d } : x)))} />}
                    {(!!quote.signature || !!quote.client_id) && <OrderRefs quote={quote} canDivalto={!quote.read_only && !quote.cancelled && (isAdmin || !!user?.sales_manager)} canRef={!quote.read_only && !quote.cancelled}
                      onChange={(d) => setQuotes((qs) => qs.map((x) => (x.id === d.id ? { ...x, ...d } : x)))}
                      onClientCode={(cid, code) => setQuotes((qs) => qs.map((x) => (x.client_id === cid ? { ...x, client_divalto_code: code } : x)))} />}
                    {quote.send_log?.length > 0 && <div><SendLog log={quote.send_log} number={quote.quote_number} /></div>}
                  </TableCell>
                  <TableCell className="px-1 md:px-4">
                    {quote.historical ? (
                      <span className="inline-block rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground whitespace-nowrap" data-testid={`historical-${quote.quote_number}`}
                        title={`${quote.hist?.salon || ""} · ${quote.hist?.kind || ""}`}>{t("Historique Excel")}</span>
                    ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-0.5 md:min-w-[92px] [&_button]:h-7 [&_button]:w-7 [&_svg]:w-3.5 [&_svg]:h-3.5" data-testid={`row-actions-${quote.quote_number}`}>
                      {!quote.read_only && !quote.cancelled && (
                        <Button size="icon" variant="ghost" onClick={() => setOcTarget(quote)} data-testid={`order-client-${quote.quote_number}`} title={t("Coordonnées client et précisions ADV")}>
                          <UserPen className="w-4 h-4" />
                        </Button>
                      )}
                      {isAdmin && quote.signature && !quote.cancelled && (
                        <Button size="icon" variant="ghost" onClick={() => setFixTarget(quote)} data-testid={`admin-correct-${quote.quote_number}`} title={t("Corriger la commande (revendeur, acompte)")}>
                          <Wrench className="w-4 h-4" />
                        </Button>
                      )}
                      {manager && (
                        <Button size="icon" variant="ghost" onClick={() => setDiscTarget(quote)} data-testid={`rdisc-${quote.quote_number}`} title={t("Remise revendeur")}>
                          <Percent className="w-4 h-4" />
                        </Button>
                      )}
                      {!mine(quote) ? (
                        quote.pdf_path ? <Button size="icon" variant="ghost" onClick={() => viewSigned(quote)} data-testid={`view-pdf-${quote.quote_number}`} title={t(quote.signature ? "Voir la commande signée" : "Voir le devis")} className={quote.signature ? "text-[#c9a227]" : ""}>
                          {quote.signature ? <FileCheck2 className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                        </Button> : null
                      ) : (<>
                      <Button size="icon" variant="ghost" onClick={() => openConfig(quote)}
                        data-testid={`open-config-${quote.quote_number}`} title={t("Ouvrir dans le configurateur")}>
                        <SlidersHorizontal className="w-4 h-4" />
                      </Button>
                      {!!quote.signature && !quote.cancelled && (
                        <Button size="icon" variant="ghost" onClick={() => modifyOrder(quote)} data-testid={`modify-order-${quote.quote_number}`}
                          title={t("Modifier la commande (annule et remplace)")} className="text-primary">
                          <FilePenLine className="w-4 h-4" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => (quote.signature ? viewSigned(quote) : openSign(quote))}
                        data-testid={`sign-${quote.quote_number}`} title={t(quote.signature ? "Voir la commande signée" : "Signer")} aria-label={t(quote.signature ? "Voir la commande signée" : "Signer")} className={quote.signature ? "text-[#c9a227]" : ""}>
                        {quote.signature ? <FileCheck2 className="w-4 h-4" /> : <PenLine className="w-4 h-4" />}
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => send(quote)}
                        data-testid={`send-${quote.quote_number}`} title={t("Envoyer")}>
                        <Mail className="w-4 h-4" />
                      </Button>
                      {!quote.signature && <Button size="icon" variant="ghost" onClick={() => copyLink(quote)}
                        data-testid={`copy-link-${quote.quote_number}`} title={t("Copier le lien vers le devis")}>
                        <Link2 className="w-4 h-4" />
                      </Button>}
                      <Button size="icon" variant="ghost" onClick={() => duplicate(quote.id)}
                        data-testid={`duplicate-${quote.quote_number}`} title={t("Dupliquer")}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      {quote.signature ? (user?.role === "admin" ? (
                        <ConfirmDeleteSigned number={quote.quote_number} onConfirm={(c) => del(quote.id, c)} testid={`delete-${quote.quote_number}`}>
                          <Button size="icon" variant="ghost" data-testid={`delete-${quote.quote_number}`} title="Supprimer la commande signée (administrateur)" className="text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </ConfirmDeleteSigned>
                      ) : (
                        <Button size="icon" variant="ghost" disabled data-testid={`delete-${quote.quote_number}`} title="Commande signée : suppression réservée à l'administrateur">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )) : (
                      <ConfirmDelete label="ce devis" name={quote.quote_number} note="Il sera conservé 30 jours dans la corbeille." onConfirm={() => del(quote.id)} testid={`delete-${quote.quote_number}`}>
                        <Button size="icon" variant="ghost"
                          data-testid={`delete-${quote.quote_number}`} title={t("Supprimer")} className="text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </ConfirmDelete>
                      )}
                      </>)}
                    </div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-sm whitespace-nowrap" data-testid={`quote-number-${quote.quote_number}`}>{quote.quote_number}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <RibDialog defaultAttach={!!ribQuote?.signature && Number(ribQuote?.deposit) > 0} open={!!ribQuote} onOpenChange={(o) => { if (!o) setRibQuote(null); }} onConfirm={(p, b) => { const q = ribQuote; setRibQuote(null); doSend(q, p, b); }} />

      <PosterPreview pdf={orderPdf} onClose={() => setOrderPdf(null)} testid="order-preview" printable title={t("Commande signée")} description={orderPdf?.paper ? t("Commande papier envoyée à vous et à contact@fautras.com (pas au client). Corinne la recevra une fois l'acompte reçu et le financement choisi.") : `${t("Voici le PDF que le client peut télécharger depuis l'e-mail envoyé à")} ${orderPdf?.email || t("son adresse")}.`} />
      <OrderClientDialog quote={ocTarget} onClose={() => setOcTarget(null)}
        onSaved={(id, p) => { setOcTarget(null); setQuotes((qs) => qs.map((x) => (x.id === id ? { ...x, ...p, client_name: p.client ? [p.client.name, p.client.firstname].filter(Boolean).join(" ") : x.client_name } : x))); }} />
      <AdminCorrectDialog quote={fixTarget} onClose={() => setFixTarget(null)} onSaved={() => { setFixTarget(null); load(); }} />
      <ResellerDiscountDialog quote={discTarget} onClose={() => setDiscTarget(null)}
        onSaved={(id, d) => { setQuotes((qs) => qs.map((x) => (x.id === id ? { ...x, ...d } : x))); setDiscTarget(null); }} />
      <VehicleSourceDialog quote={srcTarget} onClose={() => setSrcTarget(null)} onConfirm={saveSource} t={t} />
      <CopyLinkConfirm quote={copyTarget} onClose={() => setCopyTarget(null)} onGo={(q) => { setCopyTarget(null); copyLink(q); }} t={t} />
      <SignView open={!!signTarget} onOpenChange={(o) => !o && setSignTarget(null)} quote={signTarget}
        confirmLabel="Enregistrer la signature" onConfirm={saveSignature} testid="sign-save" onPaperConfirm={signTarget?.stock_order ? undefined : savePaper} />
    </div>
  );
}
